/*
 * File: onboardingApi.ts
 *
 * OnboardingApi: RTK Query APIs for the onboarding process.
 *
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AxiosError } from "axios";
import { PageContent } from "../types";
import {
  buildStrippedJsonFromValues,
  getRecaptchaToken,
  initAndGetSeonPayload,
  transformAxiosToFetchBaseQueryErrorWithLocationRedirect,
} from "../utils";
import {
  apiOnboardingKeyName,
  apiOnboardingKeyValue,
  endpoints,
  getOnboardingFetcher,
} from "./ApiConfig";

export const onboardingApi = createApi({
  reducerPath: "onboardingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: endpoints.onboarding,
    prepareHeaders: headers => {
      if (apiOnboardingKeyName && apiOnboardingKeyValue) {
        headers.set(apiOnboardingKeyName, apiOnboardingKeyValue);
      }
      return headers;
    },
  }),
  tagTypes: ["Onboarding"],
  endpoints: builder => ({
    start: builder.mutation<
      PageContent,
      {
        queryString: string;
        analyticsId?: string;
        productType?: string;
      }
    >({
      async queryFn({ queryString, analyticsId, productType = "" }) {
        try {
          await initAndGetSeonPayload();
          const res = await getOnboardingFetcher(
            undefined,
            undefined,
            true,
          ).post(
            "/start",
            {
              productType,
              queryString,
              analyticsId,
            },
            {
              withCredentials: true,
            },
          );
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
            true,
          );
          return { error };
        }
      },
    }),
    next: builder.mutation<
      PageContent,
      {
        stepId?: number;
        data: PageContent;
        values: Record<string, unknown>;
      }
    >({
      async queryFn(arg) {
        try {
          const gRecaptchaResponse = await getRecaptchaToken();
          const seonPayload = await initAndGetSeonPayload();
          const parsedData = buildStrippedJsonFromValues({
            pageContent: arg.data,
            values: arg.values,
            filterByVisibility: true,
          });
          const res = await getOnboardingFetcher(
            {
              gRecaptchaResponse,
              seonPayload,
            },
            undefined,
            true,
          ).patch("/next", parsedData, { withCredentials: true });

          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
    back: builder.mutation<PageContent, { stepId?: number; processId: string }>(
      {
        async queryFn({ processId }) {
          const headers:
            | Headers
            | string[][]
            | Record<string, string | undefined> = {};
          try {
            const res = await getOnboardingFetcher().get(`back/${processId}`, {
              withCredentials: true,
              headers,
            });
            return { data: res?.data };
          } catch (e) {
            const error =
              transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
                e as AxiosError,
                true,
              );
            return { error };
          }
        },
      },
    ),
  }),
});

export const { useStartMutation, useBackMutation, useNextMutation } =
  onboardingApi;

export const allOnboardingFulfilledMatchers = [
  onboardingApi.endpoints.back.matchFulfilled,
  onboardingApi.endpoints.next.matchFulfilled,
  onboardingApi.endpoints.start.matchFulfilled,
];

export const allOnboardingRejectedMatchers = [
  onboardingApi.endpoints.back.matchRejected,
  onboardingApi.endpoints.next.matchRejected,
  onboardingApi.endpoints.start.matchRejected,
];

export const allOnboardingPendingMatchers = [
  onboardingApi.endpoints.back.matchPending,
  onboardingApi.endpoints.next.matchPending,
  onboardingApi.endpoints.start.matchPending,
];
